import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import _isEqual from 'lodash/isEqual'
import { FormattedMessage } from 'react-intl'

import Dateblock from './Dateblock'
import Eventform from './Eventform'
import { CapitalButton } from '../../Navigation/Buttons'

// HOC
import withAppStore from '../../../../util/withAppStore'

import styles from '../../../../../css/timeblock.module.css'
import deviceStyles from '../../../../../css/devicePage.module.css'

import AppDispatcher from '../../../../dispatchers/AppDispatcher'

const MINUTES_MAXIMUM = 7 * 24 * 60
const MINUTES_MINIMUM = 0

const scheduleRing = (input) => {
  if (input >= MINUTES_MAXIMUM) {
    return input - MINUTES_MAXIMUM
  }

  if (input < MINUTES_MINIMUM) {
    return 0
  }

  return input
}

const Scheduler = ({
  devices,
  params: { settingNo },
  limit,
  isSelect,
  stopSelect,
  setChanged,
  level,
}) => {
  const [showEventForm, setShowEventForm] = useState(false)
  const [showTooManyEvents, setShowTooManyEvents] = useState(false)
  const [timeblocks, setTimeblocks] = useState({ 1: [], 2: [] })
  const [updateIndex, setUpdateIndex] = useState(-1)
  const [eventFormDay, setEventFormDay] = useState((new Date()).getDay())
  const [outletID, setOutletID] = useState(undefined)
  const [updateOutletID, setUpdateOutletID] = useState(undefined)
  const [selectedItems, setSelectedItems] = useState({})

  // Initialize timeblocks
  useEffect(() => {
    const {
      devices: [device],
    } = { devices }

    const {
      interface_versioned: {
        data: {
          other: {
            outletID: outletIDDevice = settingNo,
            scheduleType,
          } = {},
        } = {},
      } = {},
    } = device

    let timeblocks = { 1: [], 2: [] }
    if (
      device.application_settings_new &&
      device.application_settings_new.status === 'new' &&
      device.application_settings_new.settings &&
      device.application_settings_new.settings.timeblocks &&
      (!settingNo || device.application_settings_new.settings.timeblocks[settingNo])
    ) {
      timeblocks = device.application_settings_new.settings.timeblocks
    } else if (
      device.application_settings &&
      device.application_settings.settings &&
      device.application_settings.settings.timeblocks
    ) {
      timeblocks = device.application_settings.settings.timeblocks
    }

    if (devices.length > 1) {
      timeblocks = { [outletIDDevice]: [] }
    } else if (outletIDDevice && outletIDDevice === 'fertigation') {
      timeblocks = { [outletIDDevice]: timeblocks['1'] }
    } else if (outletIDDevice && timeblocks[outletIDDevice] && scheduleType !== 'pump2') {
      timeblocks = { [outletIDDevice]: timeblocks[outletIDDevice] }
    }

    setTimeblocks(timeblocks)
    setOutletID(outletIDDevice)
  }, [devices, settingNo])

  // Handle isSelect changes
  useEffect(() => {
    if (!isSelect) {
      setSelectedItems({})
    }
  }, [isSelect])

  const addEvent = useCallback((events) => {
    const timeblocksNew = { ...timeblocks }
    events.forEach((event) => {
      const {
        day, hr, min, duration, noEarlier = 0, noLater = 0, outletID, flags, fertigationDifference,
      } = event

      if (!outletID) {
        console.error('No outlet ID')
        return
      }

      if (!timeblocksNew[outletID] || !Array.isArray(timeblocksNew[outletID])) {
        timeblocksNew[outletID] = []
      }

      let start = day * 24 * 60
      start += hr * 60
      start += min
      let stop = start + duration

      const utcOffset = new Date().getTimezoneOffset()
      start += utcOffset
      stop += utcOffset

      start = scheduleRing(start)
      stop = scheduleRing(stop)

      timeblocksNew[outletID].push({
        start, stop, noEarlier, noLater, flags, fertigationDifference,
      })
    })
    setTimeblocks(timeblocksNew)
    eventFormOff()
  }, [timeblocks])

  const updateEvent = useCallback(({
    outletID, key, day, hr, min, duration, noEarlier = 0, noLater = 0, flags, fertigationDifference,
  }) => {
    let start = day * 24 * 60
    start += hr * 60
    start += min
    let stop = start + duration

    const utcOffset = new Date().getTimezoneOffset()
    start += utcOffset
    stop += utcOffset

    start = scheduleRing(start)
    stop = scheduleRing(stop)

    const timeblocksNew = { ...timeblocks }
    timeblocksNew[outletID].splice(key, 1, {
      start, stop, noEarlier, noLater, flags, fertigationDifference,
    })
    setTimeblocks(timeblocksNew)
    eventFormOff()
  }, [timeblocks])

  const removeEvent = useCallback(({ outletID, key }) => {
    const timeblocksNew = { ...timeblocks }
    timeblocksNew[outletID].splice(key, 1)
    setTimeblocks(timeblocksNew)
    eventFormOff()
  }, [timeblocks])

  const removeEventsMultiple = useCallback(() => {
    const newTimeblocks = { ...timeblocks }
    const toDelete = Object.keys(selectedItems).filter((i) => selectedItems[i] === true)
    toDelete.sort((a, b) => (a - b))
    if (!newTimeblocks[updateOutletID] || toDelete.length < 1) {
      setSelectedItems({})
      stopSelect()
      return
    }

    for (let i = toDelete.length - 1; i >= 0; i--) newTimeblocks[updateOutletID].splice(toDelete[i], 1)

    setSelectedItems({})
    setTimeblocks(newTimeblocks)
    stopSelect()
    appDispatch(true)
  }, [selectedItems, timeblocks, updateOutletID, stopSelect])

  const appDispatch = useCallback((changed = false) => {
    const timeblocksSettings = []

    for (let i = 0; i < devices.length; i += 1) {
      const device = devices[i]
      const {
        address,
        id: deviceId,
      } = device
      const {
        interface_versioned: {
          data: {
            other: {
              outletID: outletIDDevice = settingNo,
              scheduleType,
            } = {},
          } = {},
        } = {},
      } = device

      let newTimeblocks

      if (outletID === 'fertigation') {
        newTimeblocks = { 1: [], 2: [] }
        newTimeblocks['1'] = timeblocks[outletID]
        const tbCopy = JSON.parse(JSON.stringify(timeblocks[outletID]))
        newTimeblocks['2'] = tbCopy
          .filter((t) => {
            if (
              Array.isArray(t.flags) &&
              t.flags.findIndex((f) => f === 'fertigation_master') >= 0 &&
              Array.isArray(t.fertigationDifference) &&
              t.fertigationDifference.length >= 2
            ) {
              return true
            }
            return false
          })
          .map((t) => {
            const index = t.flags.findIndex((f) => f === 'fertigation_master')
            t.flags.splice(index, 1, 'fertigation')
            t.start = Number(t.start) + Number(t.fertigationDifference[0])
            t.stop = Number(t.stop) - Number(t.fertigationDifference[1])
            return t
          })
      } else if (outletID && scheduleType !== 'pump2') {
        newTimeblocks = { [outletIDDevice]: timeblocks[outletID] }
      } else if (!outletID && outletIDDevice && scheduleType !== 'pump2') {
        newTimeblocks = { [outletIDDevice]: timeblocks['1'] }
      } else {
        newTimeblocks = { ...timeblocks }
      }

      const removedNoLater = {}
      Object.keys(newTimeblocks).forEach((j) => {
        if (!newTimeblocks[j]) return
        const filtered = newTimeblocks[j].filter((v) => (!v.noLater || (v.noLater && v.noLater > new Date().getTime() / 1000)))
        removedNoLater[j] = filtered
      })

      timeblocksSettings.push({
        address,
        deviceId,
        status: 'new',
        settings: {
          timeblocks: removedNoLater,
        },
      })
    }

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: timeblocksSettings,
    })

    if (changed) {
      setChanged(true)
    }
  }, [devices, settingNo, timeblocks, outletID, setChanged])

  // Initial appDispatch
  useEffect(() => {
    appDispatch()
  }, [appDispatch])

  const handleUpdateEvent = useCallback((i, outletID) => {
    const event = { ...i }
    if (!outletID || !Array.isArray(timeblocks[outletID]) || timeblocks[outletID].length < 1) {
      setShowEventForm(false)
      return
    }
    const updateIndex = timeblocks[outletID].findIndex((j) => _isEqual(j, event))
    setShowEventForm(true)
    setUpdateIndex(updateIndex)
    setUpdateOutletID(outletID)
  }, [timeblocks])

  const eventFormOn = useCallback((day) => {
    if (
      timeblocks &&
      timeblocks[outletID] &&
      timeblocks[outletID].length > limit) {
      tooManyEventsFormOn()
    } else if (level >= 10) {
      setShowEventForm(true)
      setUpdateIndex(-1)
      setEventFormDay(day >= 0 && day <= 6 ? day : (new Date()).getDay())
    }
  }, [timeblocks, outletID, limit, level])

  const eventFormOff = useCallback(() => {
    setShowEventForm(false)
    setUpdateIndex(-1)
    setEventFormDay((new Date()).getDay())
    appDispatch(true)
  }, [appDispatch])

  const tooManyEventsFormOn = useCallback(() => {
    setShowTooManyEvents(true)
  }, [])

  const tooManyEventsFormOff = useCallback(() => {
    setShowTooManyEvents(false)
  }, [])

  const selected = useCallback((i, outletID) => {
    const event = { ...i }
    const updateIndex = timeblocks[outletID].findIndex((j) => _isEqual(j, event))
    const newSelectedItems = { ...selectedItems }
    if (!(updateIndex in newSelectedItems)) {
      newSelectedItems[updateIndex] = true
    } else {
      newSelectedItems[updateIndex] = !newSelectedItems[updateIndex]
    }
    setSelectedItems(newSelectedItems)
    setUpdateOutletID(outletID)
  }, [timeblocks, selectedItems])

  const dateArray = []
  const weekB = moment.utc().startOf('week')
  for (let i = 0; i < 7; i++) {
    const begWeek = moment().startOf('week')
    if (begWeek.valueOf() < weekB.valueOf()) begWeek.add(1, 'week')
    const dayB = moment(begWeek).add(i, 'days')
    const dayE = moment(begWeek).add(i, 'days').endOf('day')
    let repeatDayBegin = dayB.diff(weekB, 'minutes')
    if (dayB.diff(weekB, 'minutes') > MINUTES_MAXIMUM) repeatDayBegin = dayB.diff(weekB, 'minutes') - MINUTES_MAXIMUM
    else if (dayB.diff(weekB, 'minutes') < 0) repeatDayBegin = dayB.diff(weekB, 'minutes') + MINUTES_MAXIMUM

    let repeatDayEnd = dayE.diff(weekB, 'minutes')
    if (dayE.diff(weekB, 'minutes') > MINUTES_MAXIMUM) repeatDayEnd = dayE.diff(weekB, 'minutes') - MINUTES_MAXIMUM
    else if (dayE.diff(weekB, 'minutes') < 0) repeatDayEnd = dayE.diff(weekB, 'minutes') + MINUTES_MAXIMUM

    dateArray[i] = {
      day: Number(dayB.format('d')),
      dayOfWeek: dayB.format('ddd'),
      dayBegin: dayB.toISOString(),
      dayEnd: dayE.toISOString(),
      repeatDayBegin,
      repeatDayEnd,
      timeblocks: {},
      today: moment().isBetween(dayB, dayE),
    }

    Object.keys(timeblocks).forEach((j) => {
      if (outletID && outletID !== j) {
        return
      }
      const related = timeblocks[j]
        .map((k, idx) => {
          k.selected = !!selectedItems[idx]
          return k
        })
        .filter((k) => {
          const { start } = k
          let { stop } = k
          if (k.stop < k.start) stop = k.stop + MINUTES_MAXIMUM

          if (repeatDayBegin < repeatDayEnd) {
            return (
              (start >= repeatDayBegin && start <= repeatDayEnd) || // start is within
              (stop >= repeatDayBegin && stop <= repeatDayEnd) || // stop is within
              (start < repeatDayBegin && stop > repeatDayEnd) || // block crosses days
              (stop >= MINUTES_MAXIMUM && stop - MINUTES_MAXIMUM > repeatDayBegin)
            )
          }
          return (
            (start >= repeatDayBegin && start <= repeatDayEnd + MINUTES_MAXIMUM) ||
            (stop >= repeatDayBegin && stop <= repeatDayEnd + MINUTES_MAXIMUM) ||
            (start < repeatDayBegin && stop > repeatDayEnd + MINUTES_MAXIMUM) ||
            (start >= 0 && start <= repeatDayEnd) ||
            (stop >= 0 && stop <= repeatDayEnd) ||
            (start < 0 && stop > repeatDayEnd)
          )
        })
      dateArray[i].timeblocks[j] = related
    })
  }

  return (
    <div>
      <div className={styles.scheduler}>
        {(!showEventForm) && (
          <div>
            <div style={{ height: '10px' }} />
            <div className={styles.scheduler_dates}>
              {
                dateArray.map((i) => (
                  <Dateblock
                    key={i.day}
                    label={i.dayOfWeek}
                    day={i.day}
                    timeblocks={i.timeblocks}
                    today={i.today}
                    dayBegin={i.dayBegin}
                    dayEnd={i.dayEnd}
                    repeatDayBegin={i.repeatDayBegin}
                    repeatDayEnd={i.repeatDayEnd}
                    handleUpdateEvent={handleUpdateEvent}
                    newEvent={eventFormOn}
                    outletID={outletID}
                    isSelect={isSelect}
                    selected={selected}
                  />
                ))
              }
            </div>
            <div style={{ height: '20px' }} />
          </div>
        )}
        {showEventForm && (
          <div className={styles.scheduler_form_background}>
            <div className={styles.scheduler_form}>
              <div className={styles.scheduler_form_nav}>
                <button
                  className={styles.scheduler_form_nav_chevron}
                  onClick={eventFormOff}
                  type='button'
                >
                  <i className='fa fa-chevron-left fa-lg' aria-hidden='true' />
                </button>
                <span>Edit Time Block</span>
                <span className={styles.scheduler_form_nav_space} />
              </div>
              <Eventform
                timeblocks={timeblocks}
                addEvent={addEvent}
                updateEvent={updateEvent}
                removeEvent={removeEvent}
                updateIndex={updateIndex}
                eventFormDay={eventFormDay}
                outletID={outletID || updateOutletID}
              />
            </div>
          </div>
        )}
        {showTooManyEvents && (
          <div className={styles.scheduler_form_background}>
            <div className={styles.scheduler_form}>
              <div className={styles.scheduler_form_nav}>
                <button
                  className={styles.scheduler_form_nav_chevron}
                  onClick={tooManyEventsFormOff}
                  type='button'
                >
                  <i className='fa fa-chevron-left fa-lg' aria-hidden='true' />
                </button>
                <span>Max Timeblocks</span>
                <span className={styles.scheduler_form_nav_space} />
              </div>
              <h4
                style={{
                  padding: '30px',
                  lineHeight: '30px',
                  maxWidth: '460px',
                  margin: 'auto',
                }}
              >
                This device allows for up to
                &nbsp;
                {limit}
                &nbsp;
                events per schedule. You have used all available events. Please delete unused events or contact&nbsp;
                <a href='mailto:support@altrac.io'>support@altrac.io</a>
                &nbsp;
                for more information.
              </h4>
            </div>
          </div>
        )}
      </div>
      <div>
        {
          isSelect && (
            <FormattedMessage
              id='setting.remove'
              defaultMessage='REMOVE'
              description='REMOVE button'
            >
              {(txt) => (
                <CapitalButton
                  text={txt}
                  buttonFunction={removeEventsMultiple}
                  hSize='h2'
                  style={deviceStyles.saveButton}
                />
              )}
            </FormattedMessage>
          )
        }
      </div>
    </div>
  )
}

Scheduler.propTypes = {
  devices: PropTypes.array.isRequired,
  params: PropTypes.shape({
    settingNo: PropTypes.string,
  }),
  limit: PropTypes.number.isRequired,
  isSelect: PropTypes.bool,
  stopSelect: PropTypes.func,
  setChanged: PropTypes.func,
  level: PropTypes.number.isRequired,
}

export default withAppStore(Scheduler)
